import { Reducer } from "redux";
import { FenoDeviceSerialNumber } from "../../Types/Common/src/ids";
import { NioxFenoView } from "../../containers/Kiosk/NioxFeno/types";
import { NioxStatusReport } from "../../Types/Common/src/vendors/feno";
import { RESET_NIOX_FENO_STATE, SET_NIOX_FENO_CONNECTION_ATTEMPTS, SET_NIOX_FENO_CONNECTION_UNSTABLE, SET_NIOX_FENO_IPC_INITIALIZED, SET_NIOX_FENO_PAIRED_FENO_DEVICE_SERIAL_NUMBER, SET_NIOX_FENO_SIMULATE_CONNECTION, SET_NIOX_FENO_SIMULATE_MEASUREMENT, SET_NIOX_FENO_STATUS_REPORT, SET_NIOX_FENO_VIEW, SET_FENO_TYPE, SET_LANGUAGE_CODE, SET_FENO_TRANSLATIONS, SET_FENO_DEFAULT_TRANSLATIONS, SET_FENO_COLLECTIONID, SET_LABELS_MEASUREMENT, SET_FENO_VISIT_LABELS, SET_FENO_IQC_PERSIST_STATUS } from "./actionTypes";
import { ISOString } from "../../Types/Common/src/primitives";
import { CollectionID } from "../../Types/Common/src/ids";
import { LabelsOnlyMeasurement } from "../../Types/Common/src/measurement";
import { NioxConnectionType, NioxDeviceModel } from "../../Types/Common/src/kiosk";

export type NioxFenoDeviceConfig = {
  deviceModel: NioxDeviceModel;
  connectionType: NioxConnectionType;
};

export type NioxFenoState = {
  /** 
   * Represents a unique device connected session. It is a timestamp so we can track when the state was reset to when API requests with device info start being made.
   * Could prove useful for analytics (e.g., did the device connection unexpectedly drop and they had to retry a measurement multiple times?) 
   */
  sessionID: ISOString;
  ipcInitialized: boolean;
  view: NioxFenoView;
  pairedFenoDeviceSerialNumber: FenoDeviceSerialNumber | "";
  connectionAttempts: number;
  statusReport: NioxStatusReport | null;
  connectionUnstable: boolean;
  simulateConnection: "successful" | "successful-unstable" | undefined;
  simulateMeasurement: 'successful-measurement' | 'error-measurement' | 'successful-measurement-skip' | undefined;
  fenoType: 'niox' | 'nobreath' | undefined;
  /** Snapshot of the kiosk's persisted NIOX configuration for this test session. */
  nioxDeviceConfig: NioxFenoDeviceConfig | undefined;
  languageCode: string;
  translations: {};
  defaultTranslations: {};
  collectionID: CollectionID | undefined;
  labelsMeasurement: LabelsOnlyMeasurement | undefined;
  selectedVisitLabels: string[] | null;
  fenoIqcPersistStatus: 'IDLE' | 'PERSISTING' | 'PERSISTED' | 'FAILED';
};

const get_INITIAL_STATE = (): NioxFenoState => {
  return {
    sessionID: new Date().toISOString(),
    ipcInitialized: false,
    view: "INITIALIZE",
    pairedFenoDeviceSerialNumber: "",
    connectionAttempts: 0,
    statusReport: null,
    connectionUnstable: false,
    simulateConnection: undefined,
    simulateMeasurement: undefined,
    fenoType: undefined,
    nioxDeviceConfig: undefined,
    languageCode: 'en-us',
    translations: {},
    defaultTranslations: {},
    collectionID: undefined,
    labelsMeasurement: undefined,
    selectedVisitLabels: null,
    fenoIqcPersistStatus: 'IDLE'
  };
};

export type NioxFenoActionType =
  | {
    type: typeof SET_NIOX_FENO_IPC_INITIALIZED;
    payload: true;
  }
  | {
    type: typeof SET_NIOX_FENO_VIEW;
    payload: NioxFenoState['view'];
  }
  | {
    type: typeof SET_NIOX_FENO_PAIRED_FENO_DEVICE_SERIAL_NUMBER;
    payload: NioxFenoState['pairedFenoDeviceSerialNumber'];
  }
  | {
    type: typeof SET_NIOX_FENO_CONNECTION_ATTEMPTS;
    payload: NioxFenoState['connectionAttempts'];
  }
  | {
    type: typeof SET_NIOX_FENO_STATUS_REPORT;
    payload: NioxFenoState['statusReport'];
  }
  | {
    type: typeof SET_NIOX_FENO_CONNECTION_UNSTABLE;
    payload: NioxFenoState['connectionUnstable'];
  }
  | {
    type: typeof SET_NIOX_FENO_SIMULATE_CONNECTION;
    payload: NioxFenoState['simulateConnection'];
  }
  | {
    type: typeof SET_NIOX_FENO_SIMULATE_MEASUREMENT;
    payload: NioxFenoState['simulateMeasurement'];
  }
  | {
    type: typeof RESET_NIOX_FENO_STATE;
    payload: Partial<NioxFenoState>;
  }
  | {
    type: typeof SET_FENO_TYPE;
    payload: NioxFenoState['fenoType'];
  }
  | {
    type: typeof SET_LANGUAGE_CODE;
    payload: NioxFenoState['languageCode'];
  }
  | {
    type: typeof SET_FENO_TRANSLATIONS;
    payload: NioxFenoState['translations'];
  }
  | {
    type: typeof SET_FENO_DEFAULT_TRANSLATIONS;
    payload: NioxFenoState['defaultTranslations'];
  }
  | {
    type: typeof SET_FENO_COLLECTIONID;
    payload: NioxFenoState['collectionID'];
  }
  | {
    type: typeof SET_LABELS_MEASUREMENT;
    payload: NioxFenoState['labelsMeasurement'];
  }
  | {
    type: typeof SET_FENO_VISIT_LABELS;
    payload: string[] | null;
  }
  | {
    type: typeof SET_FENO_IQC_PERSIST_STATUS;
    payload: NioxFenoState['fenoIqcPersistStatus'];
  }

const canSimulate = () => {
  const canSimulate = process.env.REACT_APP_STAGE === "test";
  if (!canSimulate) {
    console.error("ERROR: cannot simulate niox feno on non-test stage!!! Check rendering logic to figure out how this code path was hit.");
  }
  return canSimulate;
};

export const nioxFenoReducer: Reducer<NioxFenoState, NioxFenoActionType> = (state = get_INITIAL_STATE(), action: NioxFenoActionType) => {
  switch (action.type) {
    case SET_NIOX_FENO_IPC_INITIALIZED:
      return { ...state, ipcInitialized: action.payload };
    case SET_NIOX_FENO_VIEW:
      return { ...state, view: action.payload };
    case SET_NIOX_FENO_PAIRED_FENO_DEVICE_SERIAL_NUMBER:
      return { ...state, pairedFenoDeviceSerialNumber: action.payload };
    case SET_NIOX_FENO_CONNECTION_ATTEMPTS:
      return { ...state, connectionAttempts: action.payload };
    case SET_NIOX_FENO_STATUS_REPORT:
      return { ...state, statusReport: action.payload };
    case SET_NIOX_FENO_CONNECTION_UNSTABLE:
      return { ...state, connectionUnstable: action.payload };
    case SET_NIOX_FENO_SIMULATE_CONNECTION:
      if (!canSimulate()) {
        return state;
      }
      return { ...state, simulateConnection: action.payload };
    case SET_NIOX_FENO_SIMULATE_MEASUREMENT:
      if (!canSimulate()) {
        return state;
      }
      return { ...state, simulateMeasurement: action.payload };
    case RESET_NIOX_FENO_STATE:
      return { ...get_INITIAL_STATE(), ...action.payload };
    case SET_FENO_TYPE:
      return { ...state, fenoType: action.payload };
    case SET_LANGUAGE_CODE:
      return { ...state, languageCode: action.payload };
    case SET_FENO_TRANSLATIONS:
      return { ...state, translations: action.payload };
    case SET_FENO_DEFAULT_TRANSLATIONS:
      return { ...state, defaultTranslations: action.payload };
    case SET_FENO_COLLECTIONID:
      return { ...state, collectionID: action.payload };
    case SET_LABELS_MEASUREMENT:
      return { ...state, labelsMeasurement: action.payload };
    case SET_FENO_VISIT_LABELS:
      return { ...state, selectedVisitLabels: action.payload };
    case SET_FENO_IQC_PERSIST_STATUS:
      return { ...state, fenoIqcPersistStatus: action.payload };
    default:
      return state;
  }
};
